import React from 'react';
import PropTypes from 'prop-types';
import ReactGA from 'react-ga';

import {HoverHandler} from '@gisatcz/ptr-core';
import {ColumnChart} from '@gisatcz/ptr-charts';
import AttributesList from '../AttributesList';
import styles from '../../../data/styles';
import {IntervalsLine, SectionHeader} from '../../Legend';
import {ButtonSwitch, ButtonSwitchOption} from '@gisatcz/ptr-atoms';

function getRelativeValue(value, total, decimals) {
	if (total && value) {
		return (
			Math.round(
				((value * 100) / total + Number.EPSILON) * Math.pow(10, decimals)
			) / Math.pow(10, decimals)
		);
	} else {
		return 0;
	}
}

class RiskAssessment extends React.PureComponent {
	static propTypes = {
		feature: PropTypes.object,
		road: PropTypes.object,
		mode: PropTypes.string,
	};

	constructor(props) {
		super(props);
		this.state = {
			reliabilityRelative: true,
			displacementRelative: true,
		};

		this.onReliabilityAbsRelChange = this.onReliabilityAbsRelChange.bind(this);
		this.onDisplacementAbsRelChange = this.onDisplacementAbsRelChange.bind(
			this
		);
	}

	onReliabilityAbsRelChange(value) {
		ReactGA.event({
			category: 'Charts',
			action: 'Change zones chart range',
			label: 'charts',
		});

		this.setState({
			reliabilityRelative: value === 'relative',
		});
	}

	onDisplacementAbsRelChange(value) {
		ReactGA.event({
			category: 'Charts',
			action: 'Change zones chart range',
			label: 'charts',
		});

		this.setState({
			displacementRelative: value === 'relative',
		});
	}

	render() {
		const props = this.props;
		const mode = props.mode;
		const classes = styles.p75AttributeIntervals.attributeClasses;
		const noDataColor = styles.zoneBase.fill;

		const classesRoad = styles.velAvgRoads.attributeClasses;
		const noDataColorRoad = styles.roadBase.fill;

		return (
			<div className="eo4sdDrrInsarVietnam20-panel-section-content">
				<div className="eo4sdDrrInsarVietnam20-panel-data-wrapper">
					{props.feature || props.road ? (
						mode === 'zones' ? (
							this.renderZones()
						) : (
							this.renderRoads()
						)
					) : (
						<p className="centered">Select polygon or line...</p>
					)}
				</div>
				<div className="eo4sdDrrInsarVietnam20-legend">
					{/*<div className="eo4sdDrrInsarVietnam20-legend-section">*/}
					{/*    <SectionHeader title="Annual displacement rate [mm/yr]" subtitle="Land use block (zonal 75-percentile) / Road segment (interpolated mean)"/>*/}
					{/*    <Intervals classes={classes} noData={noDataColor}/>*/}
					{/*</div>*/}

					<div className="eo4sdDrrInsarVietnam20-legend-section">
						{props.road ? (
							<>
								<SectionHeader
									title="Annual displacement rate [mm/year]"
									subtitle="Road segment (interpolated mean)"
								/>
								<IntervalsLine
									styleProperty="outlineColor"
									classes={classesRoad}
									noData={noDataColorRoad}
									longerTails
								/>
							</>
						) : (
							<>
								<SectionHeader
									title="Annual displacement rate [mm/year]"
									subtitle="Land use block (zonal 75-percentile)"
								/>
								<IntervalsLine
									styleProperty="fill"
									classes={classes}
									noData={noDataColor}
									longerTails
								/>
							</>
						)}
					</div>
				</div>
			</div>
		);
	}

	renderZones() {
		return (
			<>
				{this.renderCharts()}
				<AttributesList featureProperties={this.props.feature?.properties} />
			</>
		);
	}

	renderRoads() {
		return (
			<>
				<AttributesList featureProperties={this.props.road?.properties} />
			</>
		);
	}

	renderCharts() {
		return (
			<div className="eo4sdDrrInsarVietnam20-risk-assessment-charts">
				{this.renderReliabilityChart()}
				{this.renderDisplacementRatesChart()}
			</div>
		);
	}

	renderReliabilityChart() {
		const attributes = this.props.feature?.properties;
		if (attributes) {
			const count =
				attributes['rel_a'] +
					attributes['rel_b'] +
					attributes['rel_c'] +
					attributes['rel_d'] +
					attributes['rel_e'] || 0;
			let data = [];

			// TODO 0 is added to draw chart grid even if no data
			data.push(
				{
					key: 'A',
					data: {
						value: attributes['rel_a'] || 0,
						relativeValue: getRelativeValue(attributes['rel_a'], count, 1),
						color: '#4487cf',
					},
				},
				{
					key: 'B',
					data: {
						value: attributes['rel_b'] || 0,
						relativeValue: getRelativeValue(attributes['rel_b'], count, 1),
						color: '#4487cf',
					},
				},
				{
					key: 'C',
					data: {
						value: attributes['rel_c'] || 0,
						relativeValue: getRelativeValue(attributes['rel_c'], count, 1),
						color: '#4487cf',
					},
				},
				{
					key: 'D',
					data: {
						value: attributes['rel_d'] || 0,
						relativeValue: getRelativeValue(attributes['rel_d'], count, 1),
						color: '#4487cf',
					},
				},
				{
					key: 'E',
					data: {
						value: attributes['rel_e'] || 0,
						relativeValue: getRelativeValue(attributes['rel_e'], count, 1),
						color: '#4487cf',
					},
				}
			);

			return (
				<div>
					<div className="eo4sdDrrInsarVietnam20-chart-small-header risk-assessment">
						PS reliability classes <span>(freq.)</span>
					</div>
					<ButtonSwitch ghost small onClick={this.onReliabilityAbsRelChange}>
						<ButtonSwitchOption
							value="relative"
							active={this.state.reliabilityRelative}
						>
							rel
						</ButtonSwitchOption>
						<ButtonSwitchOption
							value="absolute"
							active={!this.state.reliabilityRelative}
						>
							abs
						</ButtonSwitchOption>
					</ButtonSwitch>
					<HoverHandler>
						<ColumnChart
							key="risk"
							data={data}
							keySourcePath="key"
							nameSourcePath="key"
							xSourcePath="key"
							ySourcePath={
								this.state.reliabilityRelative
									? 'data.relativeValue'
									: 'data.value'
							}
							colorSourcePath="data.color"
							height={12}
							yValuesSize={2}
							xValuesSize={2.7}
							barGapRatio={0.1}
							yOptions={{
								max: this.state.reliabilityRelative ? 100 : null,
								unit: this.state.reliabilityRelative ? '%' : null,
							}}
							xLabel
							xOptions={{
								name: 'Classes',
							}}
						/>
					</HoverHandler>
				</div>
			);
		} else {
			return null;
		}
	}

	renderDisplacementRatesChart() {
		const attributes = this.props.feature?.properties;
		if (attributes) {
			const count =
				attributes['haz_0_10'] +
					attributes['haz_10_20'] +
					attributes['haz_20_30'] +
					attributes['haz_30_40'] +
					attributes['haz_40_50'] +
					attributes['haz_50__'] || 0;

			let data = [];

			// TODO 0 is added to draw chart grid even if no data
			data.push(
				{
					key: 'haz_0_10',
					data: {
						value: attributes['haz_0_10'] || 0,
						relativeValue: getRelativeValue(attributes['haz_0_10'], count, 1),
						name: '0-10',
						color: '#a1ff62',
					},
				},
				{
					key: 'haz_10_20',
					data: {
						value: attributes['haz_10_20'] || 0,
						relativeValue: getRelativeValue(attributes['haz_10_20'], count, 1),
						name: '10-20',
						color: '#f7ff03',
					},
				},
				{
					key: 'haz_20_30',
					data: {
						value: attributes['haz_20_30'] || 0,
						relativeValue: getRelativeValue(attributes['haz_20_30'], count, 1),
						name: '20-30',
						color: '#ffb300',
					},
				},
				{
					key: 'haz_30_40',
					data: {
						value: attributes['haz_30_40'] || 0,
						relativeValue: getRelativeValue(attributes['haz_30_40'], count, 1),
						name: '30-40',
						color: '#ff4a2a',
					},
				},
				{
					key: 'haz_40_50',
					data: {
						value: attributes['haz_40_50'] || 0,
						relativeValue: getRelativeValue(attributes['haz_40_50'], count, 1),
						name: '40-50',
						color: '#cc1b00',
					},
				},
				{
					key: 'haz_50__',
					data: {
						value: attributes['haz_50__'] || 0,
						relativeValue: getRelativeValue(attributes['haz_50__'], count, 1),
						name: '50+',
						color: '#990002',
					},
				}
			);

			return (
				<div>
					<div className="eo4sdDrrInsarVietnam20-chart-small-header risk-assessment">
						PS displacement rates <span>(freq.)</span>
					</div>
					<ButtonSwitch ghost small onClick={this.onDisplacementAbsRelChange}>
						<ButtonSwitchOption
							value="relative"
							active={this.state.displacementRelative}
						>
							rel
						</ButtonSwitchOption>
						<ButtonSwitchOption
							value="absolute"
							active={!this.state.displacementRelative}
						>
							abs
						</ButtonSwitchOption>
					</ButtonSwitch>
					<HoverHandler>
						<ColumnChart
							key="risk"
							data={data}
							keySourcePath="key"
							nameSourcePath="data.name"
							xSourcePath="data.name"
							ySourcePath={
								this.state.displacementRelative
									? 'data.relativeValue'
									: 'data.value'
							}
							colorSourcePath="data.color"
							height={12}
							yValuesSize={2}
							xValuesSize={2.7}
							barGapRatio={0.1}
							yOptions={{
								max: this.state.displacementRelative ? 100 : null,
								unit: this.state.displacementRelative ? '%' : null,
							}}
							xLabel
							xOptions={{
								name: 'Rates',
								unit: 'mm/year',
							}}
						/>
					</HoverHandler>
				</div>
			);
		} else {
			return null;
		}
	}
}

export default RiskAssessment;
